// ADAPTER (browser, under RLS) — BrandingStore over `app_tenant`, whose `brand`
// column is added by migration 0081.
//
// Under `/adapters/client` for the reason recorded in eslint.config.js and in
// `capabilityStore.ts`: `/adapters` is the SERVER-only entry, and importing it
// from a SPA drags service-role and Web Push modules into the browser graph.
//
// This is the ONLY file in the branding layer permitted to name the table.

import type { Branding, BrandingStore } from '../../branding.js';

/**
 * The slice of supabase-js this adapter needs — STRUCTURAL, like
 * `CapabilityRpcClient`, and for the same two reasons: it keeps
 * `@lemontaigu/platform` dependency-free (check E4), and a test can pass a
 * plain object rather than mock a client.
 */
export interface BrandingTableClient {
  from(table: 'app_tenant'): {
    select(columns: string): PromiseLike<{ data: unknown; error: { message: string } | null }>;
  };
}

interface TenantRow {
  code: unknown;
  label_fr: unknown;
  brand: unknown;
}

/** Keeps only string→string pairs. A malformed value is dropped rather than
 *  rendered: the constraint in 0081 already refuses non-hex on write, so this
 *  guards the case where the two versions disagree — an old client reading a
 *  newer row. Dropping falls back to the compiled colour, which is correct;
 *  passing an arbitrary string into a CSS property is not. */
function chromeOf(raw: unknown): Record<string, string> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v)) out[k] = v;
  }
  return out;
}

export function createSupabaseBrandingStore(client: BrandingTableClient): BrandingStore {
  return {
    async mine(): Promise<Branding | null> {
      // NO filter, deliberately — mirroring `PropertyRepository.listForActor`.
      // The `app_tenant_select` policy already narrows this to
      // `code = any(actor_tenants())` (migration 20260808120100), so the rows
      // returned ARE the caller's reach. A `.eq('code', …)` here would need a
      // tenant code the caller does not have yet, which is circular.
      const { data, error } = await client.from('app_tenant').select('code, label_fr, brand');
      if (error) throw new Error(`app_tenant: ${error.message}`);
      const rows = Array.isArray(data) ? (data as TenantRow[]) : [];
      // Exactly one tenant is the shape today, and the only shape this can
      // render. A caller reaching several is not an error — it is an operator
      // with grants across tenants — but a header shows ONE name, so the first
      // wins and the rest are ignored rather than merged into something no
      // tenant asked for.
      const row = rows[0];
      if (!row || typeof row.code !== 'string' || typeof row.label_fr !== 'string') return null;
      return { tenantCode: row.code, labelFr: row.label_fr, chrome: chromeOf(row.brand) };
    },
  };
}
